import React, { useMemo, useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  Card,
  Skeleton,
  useColorModeValue,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import baseUrl from 'Base_Url/baseUrl';

const EncrochmentBarChart = () => {
  const [encroachmentCounts, setEncroachmentCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const labelColor = useColorModeValue('#0c1016', '#E2E8F0');
  const navigate = useNavigate();

  const fetchEncroachmentCounts = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${baseUrl}/dashboard/survey-stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response) {
        setEncroachmentCounts(response.data?.data?.encroachmentCounts || {});
      }
    } catch (err) {
      setError('Failed to load encroachment data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEncroachmentCounts();
  }, []);

  const chartOptions = useMemo(() => {
    const encroachmentLabels = [
      'Raw House',
      'Concrete House',
      'Slum',
      'Govt. Employees Colony',
      'Govt. School',
      'Private School',
      'Commercial Temporary',
      'Commercial Permanent',
      'Mall',
      'Public Utilities',
      'Public Purpose',
      'Agriculture',
      'Other',
    ];

    const encroachmentKeys = [
      'rawHouse',
      'tinShed',
      'slum',
      'govtEmployeesColony',
      'govtSchool',
      'privateSchool',
      'commercialTemporary',
      'commercialPermanent',
      'mall',
      'publicUtilities',
      'publicPurpose',
      'agriculture',
      'other',
    ];

    const chartData = encroachmentKeys.map((key) => ({
      value: parseInt(encroachmentCounts?.[key]) || 0,
      itemStyle: {
        color: 'rgba(75, 192, 192, 0.7)',
        borderRadius: [6, 6, 0, 0],
      },
    }));

    return {
      title: {
        text: 'Overall Encroachment Summary',
        left: 'center',
        textStyle: { color: labelColor, fontSize: 18 },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: '#f9f9f9',
        textStyle: { color: '#333' },
      },
      grid: { left: '5%', right: '5%', bottom: '10%', containLabel: true },
      xAxis: {
        type: 'category',
        data: encroachmentLabels,
        axisLine: { lineStyle: { color: labelColor } },
        axisLabel: {
          color: labelColor,
          rotate: 30,
          fontSize: 12,
        },
      },
      yAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: labelColor } },
        axisLabel: { color: labelColor },
        splitLine: { show: false },
      },
      series: [
        {
          name: 'Encroachments',
          type: 'bar',
          barWidth: '60%',
          data: chartData,
          label: {
            show: true,
            position: 'top',
            color: labelColor,
            fontWeight: '600',
            fontSize: 12,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.15)',
            },
          },
        },
      ],
    };
  }, [encroachmentCounts, labelColor]);

  const encroachmentKeys = [
  'rawHouse',
  'tinShed',
  'slum',
  'govtEmployeesColony',
  'govtSchool',
  'privateSchool',
  'commercialTemporary',
  'commercialPermanent',
  'mall',
  'publicUtilities',
  'publicPurpose',
  'agriculture',
  'other',
];

  const handleClick = (params) => {
    const encroachments = JSON.stringify([encroachmentKeys[params.dataIndex]]);
    navigate(
      `/admin/surveylist?encroachments=${encodeURIComponent(encroachments)}`,
    );
  };

  return (
    <Card
      w={{ base: '100%', md: '100%', lg: '100%' }}
      mx="auto"
      p={2}
      bg={cardBg}
      shadow="lg"
      borderRadius="md"
      mt={4}
    >
      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}
      {loading ? (
        <Skeleton height="360px" borderRadius="md" mt={4} />
      ) : (
        <ReactECharts
          option={chartOptions}
          style={{ height: '360px', width: '100%' }}
          onEvents={{ click: handleClick }}
        />
      )}
    </Card>
  );
};

export default EncrochmentBarChart;
